import appConst from "../../Utilities/appConst.json";
import { getCatalogCode } from "./catalogCode";
import type {
  BasketItemFull,
  BasketState,
  Frame,
  PricingProduct,
  PrepareBasketItemsParams,
} from "./types";

// ── Immutable Update Helper ────────────────────────────────────────────────────

/** Returns a new array with the matching item merged with `updates`. */
export const updateBasketItem = (
  basketItems: BasketItemFull[],
  itemId: number,
  updates: Partial<BasketItemFull>
): BasketItemFull[] =>
  basketItems.map((basketItem) =>
    basketItem.item_id == itemId ? { ...basketItem, ...updates } : basketItem
  );

// ── State Selectors ────────────────────────────────────────────────────────────

/** Extracts the pricing products array from basket state. */
export const getPricingProducts = (
  basketState: BasketState
): PricingProduct[] =>
  basketState.basket?.price_list?.products ?? [];

// ── Price Group Mapping ────────────────────────────────────────────────────────

/**
 * Groups basket items by their pricing product's price_group.
 * Used to calculate quantity-based discounts across items in the same group.
 */
export const buildItemsQuantityByPriceGroupMap = (
  basketItems: BasketItemFull[],
  pricingProducts: PricingProduct[],
  productType: string
): Map<string, { id: number; quantity: number }[]> => {
  const itemsQuantityByPriceGroupMap = new Map<
    string,
    { id: number; quantity: number }[]
  >();

  for (const item of basketItems) {
    const catalogueCode = getCatalogCode(item, productType);
    const product = pricingProducts.find(
      (productItem) => productItem.catalogue_code === catalogueCode
    );

    if (!product) {
      continue;
    }

    const groupedItems =
      itemsQuantityByPriceGroupMap.get(product.price_group) ?? [];
    groupedItems.push({ id: item.item_id, quantity: item.quantity });
    itemsQuantityByPriceGroupMap.set(product.price_group, groupedItems);
  }

  return itemsQuantityByPriceGroupMap;
};

/** Sums total quantity for all items in a given price group. */
export const getPriceGroupQuantity = (
  itemsQuantityByPriceGroupMap: Map<string, { id: number; quantity: number }[]>,
  priceGroup: string
): number =>
  (itemsQuantityByPriceGroupMap.get(priceGroup) ?? []).reduce(
    (total, item) => total + item.quantity,
    0
  );

// ── Price Range Application ────────────────────────────────────────────────────

/**
 * Matches a basket item to its pricing product and applies the correct price
 * range based on total group quantity. Returns a new item with computed prices.
 */
export const applyPriceRangeToItem = ({
  item,
  pricingProducts,
  productType,
  itemsQuantityByPriceGroupMap,
}: {
  item: BasketItemFull;
  pricingProducts: PricingProduct[];
  productType: string;
  itemsQuantityByPriceGroupMap: Map<
    string,
    { id: number; quantity: number }[]
  >;
}): BasketItemFull => {
  const catalogueCode = getCatalogCode(item, productType);
  const product = pricingProducts.find(
    (productItem) => productItem.catalogue_code === catalogueCode
  );

  // No matching product — fall back to item_final_price
  if (!product || !product.price_ranges?.length) {
    return {
      ...item,
      unit_price: item.item_final_price,
      price_list: item.item_final_price,
      base_price: item.item_final_price,
      quantity_discount: 0,
      additional_discount: 0,
      is_valid: false,
    };
  }

  const itemsQuantity = getPriceGroupQuantity(
    itemsQuantityByPriceGroupMap,
    product.price_group
  );
  const firstPriceRange = product.price_ranges[0];
  const matchedPriceRange =
    product.price_ranges.find(
      (priceRange) =>
        priceRange.from_quantity <= itemsQuantity &&
        priceRange.to_quantity >= itemsQuantity
    ) ?? product.price_ranges[product.price_ranges.length - 1];

  return {
    ...item,
    unit_price: matchedPriceRange.unit_price,
    price_list: firstPriceRange.base_price,
    base_price: matchedPriceRange.base_price,
    quantity_discount:
      firstPriceRange.base_price - matchedPriceRange.base_price,
    additional_discount:
      matchedPriceRange.base_price - matchedPriceRange.unit_price,
    is_valid: true,
  };
};

// ── Frame URL (TILES only) ─────────────────────────────────────────────────────

/** Applies the frame image URL to a tile item based on its frame_color. */
export const applyFrameUrl = (
  item: BasketItemFull,
  frames: Frame[]
): BasketItemFull => {
  if (!frames?.length) {
    return item;
  }

  const frame = frames.find(
    (frameItem) => item.frame_color === frameItem.frame_color
  );

  if (!frame?.url) {
    return item;
  }

  return { ...item, itemImageUrl: frame.url };
};

// ── Short Path Upgrade Price (BOOKS with SQUARE_600 only) ──────────────────────

/**
 * Calculates the price difference between small and large square formats
 * for SQUARE_600 album items. Used to show the upgrade/downgrade banner.
 */
export const getShortPathUpgradePrice = ({
  basketItems,
  pricingProducts,
  productType,
}: {
  basketItems: BasketItemFull[];
  pricingProducts: PricingProduct[];
  productType: string;
}): number => {
  const square600Item = basketItems.find(
    (item) =>
      item.album_type === appConst.SQUARE_600 &&
      (item.format === +appConst.SMALL_SQUARE_CODE ||
        item.format === +appConst.LARGE_SQUARE_CODE)
  );

  if (!square600Item) {
    return 0;
  }

  const otherFormat =
    square600Item.format === +appConst.SMALL_SQUARE_CODE
      ? +appConst.LARGE_SQUARE_CODE
      : +appConst.SMALL_SQUARE_CODE;
  const currentProduct = pricingProducts.find(
    (productItem) =>
      productItem.catalogue_code ===
      getCatalogCode(square600Item, productType)
  );
  const previousFormatProduct = pricingProducts.find(
    (productItem) =>
      productItem.catalogue_code ===
      getCatalogCode({ ...square600Item, format: otherFormat }, productType)
  );

  if (
    !currentProduct?.price_ranges?.length ||
    !previousFormatProduct?.price_ranges?.length
  ) {
    return 0;
  }

  return Math.abs(
    currentProduct.price_ranges[0].base_price -
      previousFormatProduct.price_ranges[0].base_price
  );
};

// ── Prepare Basket Items (main orchestrator) ───────────────────────────────────

/**
 * The central function that processes all basket items:
 * 1. Builds the price-group quantity map
 * 2. Applies price ranges to each item
 * 3. Applies frame URLs (TILES only)
 * 4. Resolves the marketing stripe text
 * 5. Calculates the short-path upgrade price
 */
export const prepareBasketItems = ({
  basketItems,
  pricingProducts,
  productType,
  isNewsletter,
  frames,
  resetChecked = false,
}: PrepareBasketItemsParams): {
  items: BasketItemFull[];
  stripeText: string;
  shortPathUpgradePrice: number;
} => {
  const nextItems = (basketItems ?? []).map((item) => ({
    ...item,
    ...(resetChecked ? { isChecked: false } : {}),
  }));

  const itemsQuantityByPriceGroupMap = buildItemsQuantityByPriceGroupMap(
    nextItems,
    pricingProducts,
    productType
  );

  const pricedItems = nextItems.map((item) => {
    const pricedItem = applyPriceRangeToItem({
      item,
      pricingProducts,
      productType,
      itemsQuantityByPriceGroupMap,
    });

    // Frame URLs are only relevant for tiles
    return productType === appConst.TILES
      ? applyFrameUrl(pricedItem, frames)
      : pricedItem;
  });

  // ── Stripe text logic ──
  let stripeText = "";

  if (
    productType === appConst.BOOKS ||
    productType === appConst.CALENDARS
  ) {
    // Books/Calendars: static stripe text (hidden for newsletter users)
    stripeText = isNewsletter
      ? ""
      : appConst.MARKETING_BOOKS_DISCOUNT_SHIPPING_STRIPE_TEXT;
  }

  return {
    items: pricedItems,
    stripeText,
    shortPathUpgradePrice: getShortPathUpgradePrice({
      basketItems: pricedItems,
      pricingProducts,
      productType,
    }),
  };
};

// ── Apply to Redux State ───────────────────────────────────────────────────────

/**
 * Runs prepareBasketItems and writes the results into the Redux state draft.
 * Called from multiple reducers to keep pricing/stripe/upgrade in sync.
 */
export const applyPreparedBasketItems = (
  state: BasketState,
  basketItems: BasketItemFull[],
  options: Partial<PrepareBasketItemsParams> = {}
): BasketItemFull[] => {
  const prepared = prepareBasketItems({
    basketItems,
    pricingProducts: getPricingProducts(state),
    productType: state.productType,
    isNewsletter: state.isNewsletter,
    frames: state.frames,
    ...options,
  });

  state.basketItems = prepared.items;
  if (state.basket) {
    state.basket.items = [...prepared.items];
  }
  state.stripeText = prepared.stripeText;
  state.shortPathUpgradePrice = prepared.shortPathUpgradePrice;

  return prepared.items;
};
